import { Component, DestroyRef, EventEmitter, Input, OnInit, Output, inject, ChangeDetectionStrategy } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatInputModule } from '@angular/material/input';
import { SubTask, Task, TaskTypeForms } from '@k12/models';
import { HouseholdService } from '@k12/households';
import { GuardianChangeFormContextResponse, GuardianChangeStudent } from '@k12/models';

interface GuardianChangeFormData {
  studentId?: string | null;
  householdId?: string | null;
  accountId?: string | null;

  currentParentName?: string | null;
  students?: GuardianChangeStudent[];

  newGuardianFirstName?: string | null;
  newGuardianLastName?: string | null;
  newGuardianDateOfBirth?: string | null;
  newGuardianPid?: string | null;
  newGuardianEmail?: string | null;
  newGuardianPhone?: string | null;
  certified?: boolean | null;
}

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'lib-guardian-change-form',
  standalone: true,
  imports: [ReactiveFormsModule, MatButtonModule, MatCardModule, MatCheckboxModule, MatInputModule],
  templateUrl: './guardian-change-form.component.html',
  styleUrl: './guardian-change-form.component.scss'
})
export class GuardianChangeFormComponent implements OnInit {
  @Input() task!: Task;
  @Input() taskAction!: SubTask | null;
  @Input() taskTypeAction!: TaskTypeForms;

  @Output() submitFormData = new EventEmitter<string>();

  private readonly fb = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);
  private readonly householdService = inject(HouseholdService);

  protected form!: FormGroup;
  protected context: GuardianChangeFormData = {};

  public ngOnInit(): void {
    const raw = this.taskAction?.formData ? this.safeParseJson(this.taskAction.formData) : {};
    this.context = this.normalizeKeys(raw) as GuardianChangeFormData;

    this.initializeForm();
    this.loadGuardianChangeContext();
  }

  protected get students(): FormArray {
    return this.form.get('students') as FormArray;
  }

  protected isFormReady(): boolean {
    return this.form.valid && this.form.dirty && this.hasSelectedStudent();
  }

  protected onBack(): void {
    globalThis.history.back();
  }

  protected submitForm(): void {
    this.form.markAllAsTouched();
    this.students.markAsTouched();
    this.students.updateValueAndValidity();

    if (!this.isFormReady()) return;

    const formValue = this.form.getRawValue();

    this.submitFormData.emit(
      JSON.stringify({
        formValue: {
          ...this.context,
          ...formValue,
          selectedStudentIds:
            formValue.students
              ?.filter((student: GuardianChangeStudent) => student.selected)
              .map((student: GuardianChangeStudent) => student.studentId)
              .filter(Boolean) ?? []
        }
      })
    );
  }

  private initializeForm(): void {
    this.form = this.fb.group({
      studentId: [this.context.studentId ?? null],
      householdId: [this.context.householdId ?? null],
      accountId: [this.context.accountId ?? null],

      currentParentName: [{ value: this.context.currentParentName ?? '', disabled: true }],

      newGuardianFirstName: [this.context.newGuardianFirstName ?? '', Validators.required],
      newGuardianLastName: [this.context.newGuardianLastName ?? '', Validators.required],
      newGuardianDateOfBirth: [this.context.newGuardianDateOfBirth ?? '', Validators.required],

      newGuardianPid: [this.context.newGuardianPid ?? '', Validators.required],
      newGuardianEmail: [this.context.newGuardianEmail ?? '', [Validators.required, Validators.email]],
      newGuardianPhone: [this.context.newGuardianPhone ?? ''],
      certified: [this.context.certified ?? false, Validators.requiredTrue],

      students: this.fb.array([], this.atLeastOneStudentSelectedValidator())
    });

    this.patchStudents(this.context.students ?? []);
  }

  private loadGuardianChangeContext(): void {
    const studentId =
      this.form.get('studentId')?.value ?? this.context.studentId ?? this.task.assignedStudentId ?? null;

    if (!studentId) return;

    this.householdService
      .getGuardianChangeFormContext(studentId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: GuardianChangeFormContextResponse) => {
          this.form.patchValue(
            {
              currentParentName: this.buildFullName(res.firstName, res.middleName, res.lastName)
            },
            { emitEvent: false }
          );

          this.patchStudents(res.students ?? []);
          this.form.markAsPristine();
          this.students.updateValueAndValidity();
        },
        error: (err) => {
          console.error('Failed to load guardian change form context. ', err);
        }
      });
  }

  private patchStudents(students: GuardianChangeStudent[]): void {
    this.students.clear();

    students.forEach((student) => {
      this.students.push(this.createStudentGroup(student));
    });

    this.students.updateValueAndValidity();
  }

  private createStudentGroup(student: GuardianChangeStudent = {}): FormGroup {
    return this.fb.group({
      studentId: [student.studentId ?? null],
      firstName: [student.firstName ?? null],
      middleName: [student.middleName ?? null],
      lastName: [student.lastName ?? null],
      fullName: [student.fullName ?? this.buildFullName(student.firstName, student.middleName, student.lastName)],
      selected: [student.selected ?? false]
    });
  }

  private hasSelectedStudent(): boolean {
    return this.students.controls.some((control) => control.get('selected')?.value === true);
  }

  private atLeastOneStudentSelectedValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const formArray = control as FormArray;

      return formArray.controls.some((student) => student.get('selected')?.value === true) ? null : { required: true };
    };
  }

  private safeParseJson(raw: string): Record<string, unknown> {
    try {
      const parsed = JSON.parse(raw) as unknown;
      return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : {};
    } catch {
      return {};
    }
  }

  private normalizeKeys(raw: Record<string, unknown>): Record<string, unknown> {
    return Object.fromEntries(
      Object.entries(raw).map(([key, value]) => [key.charAt(0).toLowerCase() + key.slice(1), value])
    );
  }

  private buildFullName(firstName?: string | null, middleName?: string | null, lastName?: string | null): string {
    return [firstName, middleName, lastName].filter((name): name is string => !!name?.trim()).join(' ');
  }
}
